import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Box, Chip, Grid, Paper, Stack, Typography } from '@mui/material';
import { storePayoutsApi } from '../services/api';
import {
  FLAG_LABELS,
  HOLD_REASON_LABELS,
  PAYOUT_STATUS_COLORS,
  PAYOUT_STATUS_LABELS,
  formatDateTime,
  formatMoney,
} from './storePayoutsUtils';
import { RETURN_REASON_LABELS, RETURN_STATUS_LABELS } from './storeReturnsUtils';

/**
 * Read-only STORE-order sections of the admin Order Details page: delivery, totals and AniPaw
 * commission, fulfilment, the current/latest return and the payout. Every section renders only
 * when the order carries its data, so a store order that predates delivery pricing, fulfilment
 * tracking or held payouts looks exactly as it did before.
 */

const FULFILMENT_LABELS = {
  AWAITING_SHIPMENT: 'Awaiting shipment',
  SHIPPED: 'Shipped',
  DELIVERED: 'Delivered',
};

const DELIVERED_SOURCE_LABELS = { STORE: 'by the store', BUYER: 'confirmed by the buyer', AUTO: 'automatically' };

const present = (v) => v !== null && v !== undefined;

const Field = ({ label, children, testId, xs = 12, sm = 6, md = 3 }) => (
  <Grid item xs={xs} sm={sm} md={md}>
    <Typography variant="caption" color="text.secondary">{label}</Typography>
    <Typography variant="body2" component="div" data-testid={testId}>{children}</Typography>
  </Grid>
);

const Section = ({ title, testId, children }) => (
  <Paper sx={{ p: 3, mb: 3 }} data-testid={testId}>
    <Typography variant="h6" gutterBottom>{title}</Typography>
    {children}
  </Paper>
);

const formatEta = (min, max) => {
  if (!present(min) && !present(max)) return null;
  const lo = present(min) ? min : max;
  const hi = present(max) ? max : min;
  return lo === hi ? `${lo} business days` : `${lo}–${hi} business days`;
};

const DeliverySection = ({ order }) => {
  const a = order.deliveryAddress;
  if (!a) return null;
  const eta = formatEta(order.etaDaysMin ?? a.etaDaysMin, order.etaDaysMax ?? a.etaDaysMax);
  return (
    <Section title="Delivery" testId="store-delivery">
      <Grid container spacing={2}>
        <Field label="Address" xs={12} sm={12} md={6}>{a.addressLine}</Field>
        {a.apartment && <Field label="Apartment">{a.apartment}</Field>}
        <Field label="Contact name">{a.contactName || '-'}</Field>
        <Field label="Contact phone">{a.contactPhone || '-'}</Field>
        {eta && <Field label="Delivery time" testId="store-eta">{eta}</Field>}
        {present(a.distanceKm) && <Field label="Distance">{`${Number(a.distanceKm).toFixed(1)} km`}</Field>}
        {a.notes && <Field label="Delivery notes" xs={12} sm={12} md={12}>{a.notes}</Field>}
      </Grid>
    </Section>
  );
};

/**
 * The order's payout record (`GET /admin/store-payouts/{orderId}`), fetched for STORE orders.
 *
 * The order DTO's `payout` block has no hard deadline and no flags, so those come from this record.
 * A missing record (404: old order, never paid) just means those fields are not shown.
 */
const usePayoutRecord = (order) => {
  const [record, setRecord] = useState(null);
  const isStore = order.orderType === 'STORE';

  useEffect(() => {
    if (!isStore || !order.id) return undefined;
    let cancelled = false;
    storePayoutsApi.get(order.id)
      .then((row) => { if (!cancelled) setRecord(row); })
      .catch(() => { /* no payout record: nothing extra to show */ });
    return () => { cancelled = true; };
  }, [isStore, order.id]);

  return record;
};

/**
 * Commission: the checkout snapshot carried on the order's `payout` block (admin / store owner
 * view), else the payout record's amounts (the record has no rate).
 */
const commissionOf = (order, record) => {
  const p = order.payout || {};
  return {
    rate: p.platformFeeRate,
    amount: present(p.platformFeeAmount) ? p.platformFeeAmount : record?.commissionAmount,
    refunded: present(p.platformFeeRefundedAmount)
      ? p.platformFeeRefundedAmount
      : record?.commissionRefundedAmount,
  };
};

const TotalsSection = ({ order, commission }) => {
  const cur = order.currencyCode;
  const hasDeliveryPricing = present(order.deliveryFee);
  const hasCommission = present(commission.rate) || present(commission.amount);
  if (!hasDeliveryPricing && !hasCommission) return null;
  return (
    <Section title="Totals & Commission" testId="store-totals">
      <Grid container spacing={2}>
        {present(order.itemsSubtotal) && (
          <Field label="Items subtotal" testId="store-subtotal">{formatMoney(order.itemsSubtotal, cur)}</Field>
        )}
        {hasDeliveryPricing && (
          <Field label="Delivery fee" testId="store-delivery-fee">
            {Number(order.deliveryFee) === 0 ? 'Free' : formatMoney(order.deliveryFee, cur)}
          </Field>
        )}
        {present(order.calculatedTotal) && (
          <Field label="Total charged" testId="store-total">
            <strong>{formatMoney(order.calculatedTotal, cur)}</strong>
          </Field>
        )}
        {present(commission.rate) && (
          <Field label="AniPaw commission rate" testId="store-commission-rate">
            {`${Number(commission.rate).toFixed(2).replace(/\.?0+$/, '')}%`}
          </Field>
        )}
        {present(commission.amount) && (
          <Field label="AniPaw commission" testId="store-commission-amount">
            {formatMoney(commission.amount, cur)}
            {Number(commission.refunded) > 0 && ` (${formatMoney(commission.refunded, cur)} returned)`}
          </Field>
        )}
      </Grid>
    </Section>
  );
};

const FulfilmentSection = ({ order }) => {
  const f = order.fulfilment;
  if (!f) return null;
  const trackingText = f.tracking && [f.tracking.carrier, f.tracking.number].filter(Boolean).join(' ');
  return (
    <Section title="Fulfilment" testId="store-fulfilment">
      <Grid container spacing={2}>
        <Field label="Status" testId="fulfilment-status">
          <Stack direction="row" spacing={1} alignItems="center">
            <Chip size="small" label={FULFILMENT_LABELS[f.status] || f.status} />
            {f.late && <Chip size="small" color="error" label="Late" />}
          </Stack>
        </Field>
        <Field label="Paid">{formatDateTime(f.paidAt)}</Field>
        {f.promisedBy && <Field label="Promised by">{formatDateTime(f.promisedBy)}</Field>}
        {f.shippedAt && <Field label="Shipped">{formatDateTime(f.shippedAt)}</Field>}
        {trackingText && <Field label="Tracking" testId="fulfilment-tracking">{trackingText}</Field>}
        {f.deliveredAt && (
          <Field label="Delivered">
            {formatDateTime(f.deliveredAt)}
            {DELIVERED_SOURCE_LABELS[f.source] && ` (${DELIVERED_SOURCE_LABELS[f.source]})`}
          </Field>
        )}
        {f.returnWindowEndsAt && <Field label="Return window ends">{formatDateTime(f.returnWindowEndsAt)}</Field>}
      </Grid>
    </Section>
  );
};

const ReturnSection = ({ order }) => {
  const r = order.storeReturn;
  if (!r) return null;
  return (
    <Section title="Return request" testId="store-return">
      <Grid container spacing={2}>
        <Field label="Status" testId="return-status">
          <Chip size="small" label={RETURN_STATUS_LABELS[r.status] || r.status} />
        </Field>
        <Field label="Reason">{RETURN_REASON_LABELS[r.reason] || r.reason || '-'}</Field>
        {present(r.refundAmount) && (
          <Field label="Refund amount" testId="return-refund">{formatMoney(r.refundAmount, order.currencyCode)}</Field>
        )}
        <Field label="Requested">{formatDateTime(r.requestedAt)}</Field>
      </Grid>
      <Box sx={{ mt: 2 }}>
        <Link to="/store-returns">Open Store Returns</Link>
      </Box>
    </Section>
  );
};

const PayoutSection = ({ order, record }) => {
  const p = order.payout;
  if (!p) return null;
  const flags = p.flags || record?.flags || [];
  const hardDeadlineAt = p.hardDeadlineAt || record?.hardDeadlineAt;
  const holds = p.holdReasons || [];
  return (
    <Section title="Store payout" testId="store-payout">
      <Grid container spacing={2}>
        <Field label="Status" testId="payout-status">
          <Chip
            size="small"
            color={PAYOUT_STATUS_COLORS[p.status] || 'default'}
            label={PAYOUT_STATUS_LABELS[p.status] || p.status}
          />
        </Field>
        {present(p.amount) && <Field label="Store receives">{formatMoney(p.amount, p.currencyCode || order.currencyCode)}</Field>}
        {p.dueAt && <Field label="Due" testId="payout-due">{formatDateTime(p.dueAt)}</Field>}
        {hardDeadlineAt && <Field label="Hard deadline" testId="payout-deadline">{formatDateTime(hardDeadlineAt)}</Field>}
        {p.releasedAt && <Field label="Released" testId="payout-released">{formatDateTime(p.releasedAt)}</Field>}
        {holds.length > 0 && (
          <Field label="Hold reasons" xs={12} sm={12} md={6} testId="payout-holds">
            {holds.map((h) => HOLD_REASON_LABELS[h] || h).join(', ')}
          </Field>
        )}
        {flags.length > 0 && (
          <Field label="Flags" xs={12} sm={12} md={12} testId="payout-flags">
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {flags.map((f) => <Chip key={f} size="small" color="warning" label={FLAG_LABELS[f] || f} />)}
            </Stack>
          </Field>
        )}
      </Grid>
      <Box sx={{ mt: 2 }}>
        <Link to="/store-payouts">Open Store Payouts</Link>
      </Box>
    </Section>
  );
};

const StoreOrderSections = ({ order }) => {
  const record = usePayoutRecord(order);
  if (order.orderType !== 'STORE') return null;
  const commission = commissionOf(order, record);
  return (
    <>
      <DeliverySection order={order} />
      <TotalsSection order={order} commission={commission} />
      <FulfilmentSection order={order} />
      <ReturnSection order={order} />
      <PayoutSection order={order} record={record} />
    </>
  );
};

export default StoreOrderSections;
